<template>
  <v-container fluid>
    <div class="d-flex flex-row-reverse">
      <v-btn color="primary" class="my-5" @click="showCreateDealDialog">Create Deal</v-btn>
    </div>
    <v-row v-if="dealStats && !$vuetify.breakpoint.smAndDown">
      <v-col class="d-flex">
        <metric-card
          class="mr-1"
          style="width:100%"
          :total="dealStats.unassignedDeals"
          label="Unassigned Deals"
          dense
        />
        <metric-card class="mx-1" style="width:100%" :total="dealStats.newDeals" label="New Deals (this week)" dense />
        <metric-card
          class="mx-1"
          style="width:100%"
          :total="userText(dealStats.callsLogged)"
          text
          label="Calls Logged (this week)"
          dense
        />
        <metric-card
          class="mx-1"
          style="width:100%"
          :total="userText(dealStats.dealsWon)"
          text
          label="Deals Won (this week)"
          dense
        />
        <metric-card
          class="ml-1"
          style="width:100%"
          :total="userText(dealStats.dealsClosed)"
          text
          label="Leaderboard (30 days)"
          dense
        />
      </v-col>
    </v-row>
    <v-card class="px-5 d-flex flex-row">
      <v-select
        v-model="filters.dealTypeId_In"
        :items="dealTypes"
        item-text="title"
        item-value="id"
        multiple
        label="Type"
        class="mr-10"
        chips
      />
      <v-select
        v-model="filters.dealOwner_User_Id_In"
        multiple
        chips
        :items="dealOwnerOptions"
        label="Deal Owner"
        class="mr-10"
      />
      <v-select v-model="filters.stageId_In" :items="stages" multiple chips label="Stage" class="mr-10" />
      <v-select v-model="filters.probability_In" :items="options.probabilities" chips multiple label="Probability" />
    </v-card>
    <v-expansion-panels class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-header
          ><strong
            >Additional Filters <v-chip>{{ additionalFilterCount }}</v-chip></strong
          ></v-expansion-panel-header
        >
        <v-expansion-panel-content>
          <v-row>
            <v-col cols="12">
              <v-text-field v-model="filters.search" placeholder="Search" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="dateRange"
                :items="['', 'Last 7 days', 'Last 30 days', 'Last 3 Months', 'This Week', 'This Month', 'This Year']"
                placeholder="Date Range"
                label="Period"
              />
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-row>
      <v-col :cols="!sidePanelActive ? 12 : 8">
        <v-data-table
          :headers="dataTable.headers"
          :items-per-page="filters.first"
          :items="deals"
          hide-default-footer
          dense
          :loading="$apollo.loading"
        >
          <template v-slot:item="{ item }">
            <tr
              :class="{
                selected: selectedClient == item,
              }"
              :style="{ background: `${getBackgroundColour(item)}` }"
            >
              <td class="caption">
                <router-link :to="`/deals/${item.id}`">{{ item.created_at }}</router-link>
              </td>
              <td>
                <v-btn elevation="0" small @click="showClientPanel(item)">{{ item.client }}</v-btn>
              </td>
              <td style="width: 130px;">
                {{ item.deal_type }}
                <!-- NOTE, this icon and tooltip is SMOKE AND MIRRORS 
                We will want to at some point soon a) Djangofy this and b) set these automations up in Mail Blaze
                -->
                <v-tooltip v-if="item.deal_type === 'Cancellation'" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-bind="attrs" v-on="on">
                      mdi-email
                    </v-icon>
                  </template>
                  <span>Triggers {{ item.deal_type }} automation</span>
                </v-tooltip>
              </td>
              <td>
                <v-select
                  :value="item.stage"
                  :items="stages"
                  :key="'stage-' + item.id"
                  @input="setStage($event, item.id)"
                  class="pa-0"
                />
              </td>
              <td>
                <v-select
                  :value="item.probability"
                  :items="options.probabilities"
                  @input="setProbability($event, item.id)"
                  class="pa-0"
                />
              </td>
              <td>
                <v-btn elevation="0" small @click="showLoggedCalls(item)">
                  {{ item.phone }}
                </v-btn>
              </td>
              <td>{{ item.last_call_time | readableDateTime }}</td>
              <td>{{ item.subscription | readableSubscriptionType }}</td>
              <td>
                <v-select
                  :value="item.deal_owner_id"
                  :items="
                    dealOwners.map((element) => {
                      return { text: element.user.firstName + ' ' + element.user.lastName, value: element.id };
                    })
                  "
                  :key="'deal-owner-' + item.id"
                  @input="setDealOwner($event, item.id)"
                  class="pa-0"
                />
              </td>
              <td>
                <v-btn x-small @click="logCall(item)" class="ma-1">Log Call</v-btn>
                <v-btn x-small target="_blank" :disabled="!item.user_id" :to="`/clients/${item.user_id}`" class="ma-1"
                  >View Client</v-btn
                >
                <v-btn x-small @click="openCreateTaskDialog(item)" class="ma-1">Create Task</v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
      <v-col v-if="sidePanelActive" cols="4">
        <v-icon class="float-right" @click="sidePanelActive = false">mdi-close</v-icon>
        <user-information v-if="selectedClient.user_id" :userId="selectedClient.user_id" :compact-view="true" />
        <v-card v-else>
          <v-card-title>Client Information</v-card-title>
          <v-card-text>
            <v-simple-table>
              <tbody>
                <tr>
                  <td>Name</td>
                  <td>{{ selectedClient.client }}</td>
                </tr>
                <tr>
                  <td>Email</td>
                  <td>{{ selectedClient.email }}</td>
                </tr>
                <tr>
                  <td>Phone</td>
                  <td>{{ selectedClient.phone }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <node-paginator
          :per-page="filters.first"
          :total-entries="numberOfRecords"
          :current-page="Math.floor(filters.offset / filters.first) + 1"
          @page-change="goToPage"
        />
      </v-col>
    </v-row>
    <v-dialog v-model="loggedCallsDialog" width="100%">
      <v-toolbar>
        Logged Calls
        <v-spacer />
        <v-btn icon dark @click="loggedCallsDialog = false" color="primary">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <deal-logged-call-list
        :calls="selectedClientCallSet"
        :call-outcomes="options.callOutcomes"
        @delete-call="deleteCall($event)"
        @edit-call="editCall($event)"
        @log-call="logCall(selectedClient)"
      />
    </v-dialog>
    <v-dialog v-model="logCallDialog" width="500">
      <deal-log-call
        ref="logCallForm"
        :call-outcomes="options.callOutcomes"
        :call-types="dealConfiguration.callTypes"
        :deal-owners="dealOwners"
        :stages="dealConfiguration.stages"
        @insert-logged-call="insertLoggedCall"
        @edit-logged-call="editLoggedCall"
        @update-stage="setStage"
      />
    </v-dialog>
    <v-dialog v-model="createDealDialog" persistent width="800">
      <v-toolbar>
        Create Deal
        <v-spacer />
        <v-btn icon dark @click="createDealDialog = false" color="primary">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-sheet>
        <deal-create-new
          ref="dealCreateForm"
          :deal-owners="dealOwners"
          :stages="dealConfiguration.stages"
          :types="dealConfiguration.types"
          @result="createDealResult"
        />
      </v-sheet>
    </v-dialog>
    <v-dialog v-model="createTaskDialog" width="800">
      <task-form :userId="selectedClient ? selectedClient.user_id : null" @close="closeCreateTaskDialog" />
    </v-dialog>
  </v-container>
</template>

<script>
import { DEAL_LIST_QUERY, DEAL_TYPES_QUERY, DEAL_STATS_QUERY } from "@/graphql/queries/deal";
import { extractNodes } from "@/lib/array";
import { formatDateTime } from "@/lib/fancy_date";
import UserInformation from "@/components/client/blocks/UserInformation";
import DealManagementMixin from "@/components/deals/mixins/DealManagementMixin";
import DealLoggedCallList from "@/components/deals/components/DealLoggedCallList";
import DealLogCall from "@/components/deals/components/DealLogCall";
import DealCreateNew from "@/components/deals/components/DealCreateNew";
import { decodeId } from "@/lib/string";
import MetricCard from "@/components/shared/MetricCard";
import NodePaginator from "@/components/shared/NodePaginator";
import TaskForm from "@/components/task/components/TaskForm";

export default {
  name: "DealDashboard",
  components: {
    NodePaginator,
    MetricCard,
    UserInformation,
    DealLoggedCallList,
    DealLogCall,
    DealCreateNew,
    TaskForm,
  },
  mixins: [DealManagementMixin],
  data() {
    return {
      loadingFilters: true,
      numberOfRecords: 0,
      deals: [],
      sidePanelActive: false,
      selectedClient: null,
      selectedClientCallSet: [],
      loggedCallsDialog: false,
      createDealDialog: false,
      createTaskDialog: false,
      dealStats: null,
      dataTable: {
        headers: [
          { text: "Created At", align: "start", sortable: true, value: "created_at", filterable: false },
          { text: "Client", sortable: true, value: "client", filterable: true },
          { text: "Type", sortable: true, value: "deal_type", filterable: true },
          { text: "Stage", sortable: true, value: "stage", filterable: false },
          { text: "Probability", sortable: true, value: "probability", filterable: false },
          { text: "Phone", sortable: false, value: "phone", filterable: false },
          { text: "Last Call", sortable: false, value: "last_call_time", filterable: false },
          { text: "Subscription", sortable: false, value: "subscription", filterable: true },
          { text: "Deal Owner", sortable: true, value: "deal_owner", filterable: false },
          { text: "Actions", sortable: false, value: "actions", filterable: false },
        ],
      },
      filters: {
        first: 200,
        offset: 0,
        dealOwner_User_Id_In: [],
        dealOwner_User_Id_Isnull: null,
        dealTypeId_In: [],
        stageId_In: [],
        probability_In: [],
        createdAt_Gte: null,
        createdAt_Lte: null,
        orderBy: "-createdAt",
        search: "",
      },
      dateRange: null,
    };
  },
  computed: {
    additionalFilterCount() {
      let count = this.filters.search.length > 0 ? 1 : 0;
      if (this.dateRange) count++;

      return count;
    },
    dealOwnerOptions() {
      return this.dealOwners.map((element) => {
        return { text: element.user.firstName + " " + element.user.lastName, value: decodeId(element.nodeID) };
      });
    },
  },
  watch: {
    filters: {
      handler(newValue, oldValue) {
        newValue["dealOwner_User_Id_Isnull"] = newValue["dealOwner_User_Id_In"].length === 0;
        let q = JSON.stringify(newValue);
        window.history.replaceState(null, "", "?q=" + q);
      },
      deep: true,
    },
    dateRange(newValue, oldValue) {
      switch (newValue) {
        case "Last 7 days":
          this.filters.createdAt_Gte = this.$moment()
            .utc()
            .subtract(7, "days")
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          break;
        case "Last 30 days":
          this.filters.createdAt_Gte = this.$moment()
            .utc()
            .subtract(30, "days")
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          break;
        case "Last 3 Months":
          this.filters.createdAt_Gte = this.$moment()
            .utc()
            .subtract(3, "months")
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          break;
        case "This Week":
          this.filters.createdAt_Gte = this.$moment()
            .utc()
            .startOf("isoWeek")
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          break;
        case "This Month":
          this.filters.createdAt_Gte = this.$moment()
            .utc()
            .startOf("month")
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          break;
        case "This Year":
          this.filters.createdAt_Gte = this.$moment()
            .utc()
            .startOf("year")
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
          break;
        default:
          this.filters.createdAt_Gte = null;
          this.filters.createdAt_Lte = null;
      }
    },
  },
  created() {
    if (this.$route.query.q) {
      this.filters = JSON.parse(this.$route.query.q);
    }
    this.loadingFilters = false;
  },
  methods: {
    getBackgroundColour(item) {
      // won is green
      if (item.stage_title === "WON") {
        return "#c6e5c6 !important";
      }

      // handle other stages here.
      if (item.stage_code === "NEW" || item.stage_code === "DUPLICATE") {
        return "";
      }

      if (item.stage_code === "TRYING_TO_CONNECT") {
        return "#f5f7eb !important";
      }
      if (item.stage_code === "CONNECTED") {
        return "#ebf7ad !important";
      }
      return "";
    },
    async refreshData() {
      await this.$apollo.queries.deals.refetch();
      await this.$apollo.queries.dealStats.refetch();
    },
    showLoggedCalls(item) {
      this.selectedClient = item;
      this.selectedClientCallSet = item.callset.sort((a, b) => (a.callTime < b.callTime ? 1 : -1));
      this.loggedCallsDialog = true;
    },
    showClientPanel(item) {
      if (this.sidePanelActive && this.selectedClient == item) {
        this.sidePanelActive = false;
        return;
      }

      this.selectedClient = item;
      this.sidePanelActive = true;
    },
    async showCreateDealDialog() {
      this.createDealDialog = true;
      await this.$nextTick();
      this.$refs.dealCreateForm.init();
    },
    async updateDeal(data) {
      let result = await this.$super(DealManagementMixin).updateDeal(data);
      await this.refreshData();
      return result;
    },
    async insertLoggedCall(data) {
      await this.$super(DealManagementMixin).insertLoggedCall(data);
      await this.refreshData();
    },
    async editLoggedCall(data) {
      await this.$super(DealManagementMixin).editLoggedCall(data);
      await this.refreshData();
      this.loggedCallsDialog = false;
    },
    async deleteCall(call) {
      let deleted = await this.$super(DealManagementMixin).deleteCall(call);
      if (!deleted) return;
      let index = this.selectedClientCallSet.findIndex((object) => object.id == call.id);
      this.selectedClientCallSet.splice(index, 1);
      await this.refreshData();
      this.loggedCallsDialog = false;
    },
    async logCall(item) {
      this.logCallDialog = true;
      await this.$nextTick();
      this.$refs.logCallForm.init(item, decodeId(this.$store.getters.user.id));
    },
    openCreateTaskDialog(item) {
      this.selectedClient = item;
      this.createTaskDialog = true;
    },
    closeCreateTaskDialog() {
      this.createTaskDialog = false;
    },
    async editCall(call) {
      this.logCallDialog = true;
      await this.$nextTick();
      this.$refs.logCallForm.initEdit(this.selectedClient, call);
    },
    async createDealResult(message) {
      this.createDealDialog = false;
      this.$toasted.success(message);
      await this.refreshData();
    },
    userText(closers = []) {
      let string = "";
      closers.forEach((closer) => {
        string += `${closer.firstName} ${closer.lastName} (${closer.total}) <br>`;
      });
      return string;
    },
    goToPage(page) {
      this.filters.offset = (page - 1) * this.filters.first;
    },
  },
  apollo: {
    deals: {
      query: DEAL_LIST_QUERY,
      fetchPolicy: "network-only",
      debounce: 500,
      variables() {
        return JSON.parse(JSON.stringify(this.filters));
      },
      skip() {
        return this.loadingFilters;
      },
      result(response) {
        if (response.data && !response.loading) {
          this.numberOfRecords = response.data.deals.totalCount;
        }
      },
      update(data) {
        data = extractNodes(data.deals.edges);
        data = data.map((element) => {
          return {
            id: element.id,
            created_at: formatDateTime(element.createdAt, "ddd, DD MMM HH:MM", false),
            stage: element.stage ? element.stage.id : null,
            stage_title: element.stage ? element.stage.title : null,
            stage_code: element.stage ? element.stage.code : null,
            probability: element.probability,
            client: [element.contact.firstName, element.contact.lastName].join(" "),
            email: element.contact.email,
            subscription: element.subscription?.product?.type,
            phone: element.contact.phone + " (" + element.dealcallSet.length + ")",
            deal_owner: element.dealOwner
              ? `${element.dealOwner.user.firstName} ${element.dealOwner.user.lastName}`
              : "",
            deal_owner_id: element.dealOwner?.id,
            user_id: element.contact.user ? element.contact.user.id : null,
            callset: element.dealcallSet,
            deal_type: element.dealType?.title,
            last_call_time: element.lastCallTime,
          };
        });
        return data;
      },
    },
    dealTypes: {
      query: DEAL_TYPES_QUERY,
    },
    dealStats: {
      query: DEAL_STATS_QUERY,
      fetchPolicy: "cache-and-network",
      variables: {
        userLimit: 3,
      },
    },
  },
};
</script>

<style>
tr.selected {
  background: #7d92f5 !important;
}
</style>
